/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
// @ts-check
import {
  Box,
  Button,
  CloseIcon,
  Container,
  EditIcon,
  RatingIcon,
} from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import Dialog from '@reach/dialog';
import React from 'react';
import { jsx } from 'theme-ui';
import useSiteConfig from '../useSiteConfig';
import TastingNoteForm from './TastingNoteForm';
import { useTastingNote } from './useTastingNote';

/**
 *
 * @param {{
 *  product?: import('./useTastingNote').TastingNoteSubject;
 *  children: React.ReactNode;
 *  size?: 'default' | 'compact';
 *  [x: string]: any;
 * }} props
 */
export default function ProductTastingNoteButton({
  product,
  size,
  children,
  ...rest
}) {
  const [isOpen, open] = React.useState(false);
  const [tastingNote, actions] = useTastingNote(product);
  const { tastingNotesEnabled } = useSiteConfig();
  if (!tastingNotesEnabled) return null;
  if (!product) return null;

  return (
    <>
      <TastingNoteButton
        tastingNote={tastingNote}
        onClick={(e) => {
          e.preventDefault();
          open(true);
        }}
        size={size}
        {...rest}
      >
        {React.Children.map(children, (child) => {
          if (!child) return child;
          if (typeof child !== 'object') return child;
          // @ts-ignore
          if (!child.props) return child;
          // @ts-ignore
          return React.cloneElement(child, { tastingNote, variant: size });
        })}
      </TastingNoteButton>
      <TastingNoteDialog
        tastingNote={tastingNote}
        product={product}
        isOpen={isOpen}
        onDismiss={() => open(false)}
        onSubmit={(values) => actions?.set(values)}
        onDelete={tastingNote && (() => actions?.delete())}
      />
    </>
  );
}
ProductTastingNoteButton.Icon = TastingNoteIcon;
ProductTastingNoteButton.Text = TastingNoteText;

/**
 *
 * @param {{
 *  variant?: 'default' | 'compact';
 *  tastingNote?: import('./useTastingNote').TastingNote;
 *  [x: string]: any
 * }} props
 * @returns
 */
export function TastingNoteIcon({ variant, tastingNote, ...rest }) {
  if (tastingNote) {
    if ((variant = 'compact')) {
      return (
        <RatingIcon
          rating={tastingNote.rating}
          size="smallMedium"
          sx={{
            color: 'primary',
            borderRadius: 'round',
            backgroundColor: 'background',
          }}
          {...rest}
        />
      );
    }
    return (
      <RatingIcon
        rating={tastingNote.rating}
        size="small"
        sx={{
          color: 'primary',
          borderRadius: 'round',
          backgroundColor: 'background',
        }}
        {...rest}
      />
    );
  }
  if (variant === 'compact') {
    return <EditIcon size="small" sx={{ margin: 1 }} {...rest} />;
  }
  return <EditIcon {...rest} />;
}

function TastingNoteText(props) {
  if (props.tastingNote) return <Trans>Edit tasting note</Trans>;
  return <Trans>Add tasting note</Trans>;
}

function TastingNoteButton({ tastingNote, onClick, size, children, ...rest }) {
  if (size === 'compact') {
    return (
      <Button
        onClick={onClick}
        variant="text"
        sx={{
          padding: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          borderRadius: 'round',
          backgroundColor: 'border',
          color: 'lightText',
        }}
        {...rest}
      >
        {children}
      </Button>
    );
  }
  return (
    <Button
      onClick={onClick}
      variant="outline"
      sx={{
        paddingX: 4,
        paddingY: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

function TastingNoteDialog({
  tastingNote,
  product,
  isOpen,
  onDismiss,
  onSubmit,
  onDelete,
}) {
  return (
    <Dialog
      isOpen={isOpen}
      aria-label="Add tasting note"
      onDismiss={onDismiss}
      sx={{ margin: 0, padding: 0, width: '100%', minHeight: '100%' }}
    >
      <Box sx={{ padding: 'gutter', paddingBottom: 0 }}>
        <Button
          variant="text"
          onClick={onDismiss}
          sx={{ marginLeft: 'auto', display: 'block' }}
        >
          <CloseIcon />
        </Button>
      </Box>
      <Container>
        <TastingNoteForm
          product={product}
          tastingNote={tastingNote}
          onSubmit={(values) => {
            onSubmit(values);
            onDismiss();
          }}
          onDelete={
            onDelete &&
            (() => {
              onDelete();
              onDismiss();
            })
          }
        />
      </Container>
    </Dialog>
  );
}
